import { roundMoney } from '../money';

/** Estados en los que aún se puede registrar un pago en la venta. */
export function canProcessSalesOrderPayment(status: string): boolean {
  return !['PAID', 'CANCELLED', 'REFUNDED'].includes(status);
}

/**
 * Estados en los que se puede actualizar la entrega al cliente (handoff).
 * Aligns with backend `updateItemDelivery` / `markAsDelivered` guards.
 *
 * LP: Intentional — PARTIAL_PAID allowed (delivery ≠ full settlement).
 * Blocked when delivery was voided by a full return (`CANCELLED`).
 */
export function canUpdateSalesOrderDelivery(
  status: string,
  deliveryStatus?: string | null,
): boolean {
  if (deliveryStatus === 'CANCELLED') return false;
  return ['PAID', 'PARTIAL_PAID', 'DELIVERED'].includes(status);
}

/** Legacy pre-migration fulfillment value — treat like COMPLETED until DB is clean. */
function isFulfillmentClosedForExchange(fulfillment: string): boolean {
  return fulfillment === 'NOT_REQUIRED' || fulfillment === 'COMPLETED' || fulfillment === 'READY';
}

/** Post-sale pricing / charges editable while unpaid balance remains. */
export function canEditSalesOrderPricing(status: string, remainingBalance: number): boolean {
  if (['PAID', 'DELIVERED', 'CANCELLED', 'REFUNDED'].includes(status)) {
    return false;
  }
  return roundMoney(remainingBalance) > 0.01;
}

/**
 * Whether commercial sale date can be corrected after registration.
 * Aligns with backend `updateSaleDate` status guard.
 */
export function canEditSalesOrderSaleDate(status: string): boolean {
  return ['CONFIRMED', 'PARTIAL_PAID', 'PAID', 'DELIVERED'].includes(status);
}

/** Post-sale commercial edit (customer, invoice, seller, date, pricing if unpaid). */
export function canEditSalesOrderCommercial(status: string): boolean {
  return canEditSalesOrderSaleDate(status);
}

/**
 * Whether the exchange action should be shown / allowed.
 * Mixed sales (`PENDING` / `FAILED` / `CANCELLED`) block until logistics are closed.
 * Local-only (`NOT_REQUIRED`) and completed mixed sales are allowed when
 * payment status is eligible. Full-return voided delivery blocks exchange.
 */
export function canExchangeSalesOrder(
  status: string,
  fulfillmentStatus?: string | null,
  deliveryStatus?: string | null,
): boolean {
  const hasValidStatus = ['PAID', 'DELIVERED', 'PARTIAL_PAID'].includes(status);
  if (!hasValidStatus) return false;
  if (deliveryStatus === 'CANCELLED') return false;

  const fulfillment = fulfillmentStatus ?? 'NOT_REQUIRED';
  return isFulfillmentClosedForExchange(fulfillment);
}
